import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Building2, Cog, Wrench, AlertTriangle, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TechDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [todayVisits, setTodayVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, incidentsRes, visitsRes] = await Promise.all([
        axios.get(`${API}/stats/admin`),
        axios.get(`${API}/incidents`),
        axios.get(`${API}/visits`)
      ]);
      setStats(statsRes.data);
      setRecentIncidents(incidentsRes.data.filter(i => i.status !== 'resuelto').slice(0, 5));
      
      // Filter today's visits
      const today = new Date().toISOString().split('T')[0];
      setTodayVisits(visitsRes.data.filter(v => v.scheduled_date === today && v.status === 'programada'));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Empresas', value: stats?.companies_count || 0, icon: Building2, color: 'sky' },
    { label: 'Máquinas', value: stats?.machines_count || 0, icon: Cog, color: 'emerald' },
    { label: 'Trabajos Registrados', value: stats?.works_count || 0, icon: Wrench, color: 'violet' },
    { label: 'Incidencias Pendientes', value: stats?.incidents_pending || 0, icon: AlertTriangle, color: 'amber' },
  ];

  const getPriorityBadge = (priority) => {
    const styles = {
      baja: 'text-slate-400',
      media: 'text-amber-400',
      alta: 'text-orange-400',
      urgente: 'text-rose-400',
    };
    return <span className={`font-mono text-xs uppercase ${styles[priority]}`}>{priority}</span>;
  };

  return (
    <Layout title={`Hola, ${user?.name}`}>
      <div className="space-y-6 animate-fade-in" data-testid="tech-dashboard">
        {/* Role Banner */}
        <Card className="bg-gradient-to-r from-emerald-500/10 to-transparent border-emerald-500/20">
          <CardContent className="p-6">
            <h3 className="font-rajdhani text-2xl font-bold text-white mb-1">Panel de Técnico</h3>
            <p className="text-slate-400">Registra los trabajos realizados y consulta las incidencias</p>
          </CardContent>
        </Card>

        {/* Today's Visits */}
        {todayVisits.length > 0 && (
          <Card className="bg-sky-500/5 border-sky-500/20">
            <CardHeader className="border-b border-sky-500/20 pb-3">
              <CardTitle className="font-rajdhani text-lg text-sky-400 flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Visitas Programadas para Hoy
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {todayVisits.map((visit) => (
                  <div key={visit.id} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-sm">
                    <Building2 className="w-5 h-5 text-sky-400" />
                    <span className="text-white font-medium">{visit.company_name}</span>
                    {visit.notes && <span className="text-sm text-slate-400">- {visit.notes}</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                        {stat.label}
                      </p>
                      <p className="text-4xl font-rajdhani font-bold text-white">
                        {loading ? '-' : stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-sm bg-${stat.color}-500/10 flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 text-${stat.color}-400`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/tech/works">
            <Card className="bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-sm bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                  <Wrench className="w-7 h-7 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-rajdhani text-xl font-bold text-white">Registrar Trabajo</h3>
                  <p className="text-slate-400 text-sm">Añade un nuevo trabajo realizado</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/tech/incidents">
            <Card className="bg-slate-900/50 border-slate-800 hover:border-amber-500/50 transition-all cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-sm bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <AlertTriangle className="w-7 h-7 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-rajdhani text-xl font-bold text-white">Ver Incidencias</h3>
                  <p className="text-slate-400 text-sm">Consulta las incidencias reportadas</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Incidents */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <CardTitle className="font-rajdhani text-xl font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Incidencias Activas
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Cargando...</div>
            ) : recentIncidents.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No hay incidencias activas</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {recentIncidents.map((incident) => (
                  <div key={incident.id} className="p-4 hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getPriorityBadge(incident.priority)}
                          <span className="text-slate-600">•</span>
                          <span className="text-xs text-slate-500 font-mono">{incident.company_name}</span>
                        </div>
                        <h4 className="text-white font-medium truncate">{incident.title}</h4>
                        <p className="text-sm text-slate-400 truncate mt-1">{incident.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
